import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import queryString from 'querystring';
import root from 'window-or-global';

import {video} from '../../../../constants';
import {
    trackVideoComplete,
    trackVideoLoad,
    trackVideoPause,
    trackVideoPlay
} from '../trackingHelpers';
import {appActions} from '../../../../redux/actions';
import {TrackingEventTypes} from '../../../../constants/Tracking';
import * as YTManager from './YTManager';

const mapStateToProps = state => ({
    locale: state.getIn(['localization', 'locale'])
});

class Youtube extends PureComponent {
    static propTypes = {
        autoplay: PropTypes.bool,
        className: PropTypes.string,
        contentId: PropTypes.string,
        hasControls: PropTypes.bool,
        hasFullScreen: PropTypes.bool,
        locale: PropTypes.string,
        loop: PropTypes.bool,
        muted: PropTypes.bool,
        title: PropTypes.string,
        trackEvent: PropTypes.func
    };

    static defaultProps = {
        autoplay: false,
        hasControls: true,
        hasFullScreen: true,
        loop: false
    };

    iframe = React.createRef();

    componentDidMount() {
        const {contentId} = this.props;

        // add api if not already included on page
        YTManager.initialize();

        // subscribe to youtube api events
        YTManager.subscribe(contentId, YT => {
            this.player = new YT.Player(contentId, {
                events: {
                    onReady: this.handlePlayerOnReady,
                    onStateChange: this.handlePlayerOnStateChange
                }
            });
        });
    }

    componentWillUnmount() {
        const {contentId} = this.props;
        YTManager.unsubscribe(contentId);
        // if player is not initialized, it will not have this method
        if (this.player && this.player.removeEventListener) {
            this.player.removeEventListener('onReady', this.handlePlayerOnReady);
            this.player.removeEventListener('onStateChange', this.handlePlayerOnStateChange);
        }
        this.player = null;
    }

    handlePlayerOnReady = () => {
        trackVideoLoad(this.props);
    };

    handlePlayerOnStateChange = ({data}) => {
        const YT = root.YT;
        switch (data) {
            case YT.PlayerState.PLAYING:
                trackVideoPlay(this.props);
                break;
            case YT.PlayerState.PAUSED:
                trackVideoPause(this.props);
                break;
            case YT.PlayerState.ENDED:
                trackVideoComplete(this.props);
                break;
            default:
                // do nothing
                break;
        }
    };

    buildYoutubeSrc = () => {
        const {autoplay, contentId, hasControls, hasFullScreen, locale, loop, muted} = this.props;

        // https://developers.google.com/youtube/player_parameters
        const options = {
            autoplay: autoplay ? 1 : 0,
            controls: hasControls ? 1 : 0,
            enablejsapi: 1,
            fs: hasFullScreen ? 1 : 0,
            hl: locale,
            iv_load_policy: 3,
            loop: loop ? 1 : 0,
            modestbranding: 1,
            mute: muted ? 1 : 0,
            // the playlist is required by the loop to work correctly on some scenarios
            // https://developers.google.com/youtube/player_parameters#loop
            playlist: loop ? contentId : null,
            rel: 0
        };

        return `${video.YOUTUBE_BASE_URL}/${contentId}?${queryString.stringify(options)}`;
    };

    sendCommand = command => {
        const iframe = this.iframe.current;
        if (!iframe) return;
        const message = JSON.stringify({
            event: 'command',
            func: command,
            id: iframe.id
        });

        iframe.contentWindow.postMessage(message, video.YOUTUBE_BASE_URL);
    };

    pauseVideo = () => {
        this.sendCommand(video.commands.PAUSE);
    };

    playVideo = () => {
        this.sendCommand(video.commands.PLAY);
    };

    render() {
        const {className, contentId, hasFullScreen, title} = this.props;

        return (
            <iframe
                className={className}
                id={contentId}
                title={title}
                src={this.buildYoutubeSrc()}
                frameBorder="0"
                referrerPolicy="no-referrer"
                ref={this.iframe}
                allow="autoplay; encrypted-media"
                allowFullScreen={hasFullScreen}
            />
        );
    }
}

const mapDispatchToProps = {
    trackEvent: params =>
        appActions.trackEvent(
            TrackingEventTypes.Interaction,
            'LauncherVideoPlayer',
            params.eventAction,
            params.eventLabel,
            params.eventValue
        )
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
    null,
    {forwardRef: true}
)(Youtube);
